import { type ChatMessage } from "./api";

/**
 * Mock AI-chat sender for the design showcase (WP8). Returns canned ROOK
 * answers after a simulated delay so the decision meeting can demo the
 * Intercom replacement with no backend. Same signature shape as the real
 * `sendChatMessage`, so the widget swaps senders at the call site.
 *
 * The real backend path (`api.ts`) is untouched — this file is only
 * reached when showcase mode is active.
 */

const DEFAULT_DELAY_MS = 800;

/** Topic-matched canned replies — keyed off the latest user message. */
const CANNED: { match: RegExp; answer: string }[] = [
  {
    match: /webhook/i,
    answer:
      "ROOK delivers data via webhooks: register an HTTPS endpoint in " +
      "Webhooks, pick the event types, and we POST a signed payload on each " +
      "update. Failed deliveries retry with backoff, and you can replay them " +
      "from the activity log.",
  },
  {
    match: /\bsdk\b|mobile|ios|android|react native|flutter/i,
    answer:
      "The ROOK SDKs (iOS, Android, React Native, Flutter) handle provider " +
      "auth, background sync, and permission prompts for you. Drop in your " +
      "client UUID, request the health permissions you need, and the SDK " +
      "streams normalized data up to ROOK.",
  },
  {
    match: /data (platform|structure|model)|schema|normali[sz]|metric/i,
    answer:
      "Every source is normalized into ROOK's unified health schema — sleep, " +
      "physical, body and events — so you integrate once and read the same " +
      "shape whether the user is on Garmin, Fitbit, or Apple Health.",
  },
  {
    match: /provider|connect|garmin|fitbit|oura|whoop|polar|setup|onboard/i,
    answer:
      "To connect a provider, send the user through the ROOK Connections " +
      "flow (or your own UI via the SDK). They authorize once; from then on " +
      "ROOK keeps the connection synced and surfaces its health in the " +
      "Connectors status panel.",
  },
  {
    match: /score|sleep|readiness|recovery/i,
    answer:
      "ROOK Score rolls the normalized metrics into sleep, recovery and " +
      "activity scores you can read straight from the API — no per-provider " +
      "math on your side.",
  },
];

/** Used when nothing matches — rotates so repeated asks feel alive. */
const FALLBACK = [
  "Happy to help with ROOK integration. Ask me about webhooks, the SDKs, " +
    "the data schema, or connecting a provider.",
  "ROOK gives you one API across every wearable. What part of the " +
    "integration are you working on — ingestion, delivery, or the data model?",
  "You can explore all of this in Develop → docs. Want the short version " +
    "for webhooks, the SDK, or provider setup?",
];

const pickReply = (messages: ChatMessage[]): string => {
  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  const text = lastUser?.content ?? "";
  const hit = CANNED.find((entry) => entry.match.test(text));
  if (hit) return hit.answer;
  const userTurns = messages.filter((m) => m.role === "user").length;
  const index = (Math.max(userTurns, 1) - 1) % FALLBACK.length;
  return FALLBACK[index];
};

/** Resolve a canned reply after `delayMs` (0 in tests for determinism). */
export const sendMockChatMessage = async (
  messages: ChatMessage[],
  delayMs: number = DEFAULT_DELAY_MS,
): Promise<string> => {
  await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
  return pickReply(messages);
};
